/**
 * Landing a promoted excerpt in the current object set.
 *
 * Promotion writes to the case graph, but the graph is not what the analyst is
 * looking at — the lens views render an *object set*. Without this step a
 * promoted excerpt is real and invisible: the Explore canvas samples a few
 * hundred of the case's nodes, so a node created a second ago is unlikely to be
 * among them, and the timeline renders nothing until something defines a slice.
 *
 * So promotion follows the same rule as "Add to graph" everywhere else: it
 * lands in the active investigation if one is open, and otherwise opens a
 * draft exploration — no hard gate, nothing to set up first.
 *
 * The excerpt travels with the Conversation it was taken from and the
 * EXCERPT_OF edge between them. A lone node on a canvas says nothing; the
 * anchor is what makes it readable as evidence.
 */

import type { StoreApi } from 'zustand/vanilla'
import {
  addContributionToActive,
  openAsExploration,
} from '@/components/agent/tools/_shared/applyContribution'
import type { CommsExcerpt } from '@/services/api/comms'
import type { InvestigationsState } from '@/stores/investigations-store'
import { emptyViewState, type GraphView } from '@/types/graph-view'

/** Where a promoted excerpt landed, so the caller can say so. */
export type ExcerptLanding = 'active' | 'draft' | 'none'

/** The excerpt plus its anchoring conversation, as a canvas fragment. */
export function excerptGraphView(
  excerpt: CommsExcerpt,
  caseId: string,
  conversationTitle: string
): GraphView {
  const nodes: GraphView['nodes'] = [
    {
      id: excerpt.id,
      labels: ['MessageExcerpt'],
      typeLabel: 'MessageExcerpt',
      name: excerpt.note || `Excerpt · ${excerpt.messageCount} msg`,
      properties: {
        excerpt_id: excerpt.excerptId,
        conversation_id: excerpt.conversationId,
        message_count: excerpt.messageCount,
        ts_from: excerpt.tsFrom,
        ts_to: excerpt.tsTo,
        note: excerpt.note,
        created_by: excerpt.createdBy,
      },
    },
  ]
  const edges: GraphView['edges'] = []

  if (excerpt.conversationNodeId) {
    nodes.push({
      id: excerpt.conversationNodeId,
      labels: ['Conversation'],
      typeLabel: 'Conversation',
      name: conversationTitle,
      properties: { conversation_id: excerpt.conversationId },
    })
    edges.push({
      // The real edge id whenever the server gave one: the object set dedups
      // edges by id, and once the canvas pulls the case-graph edges between its
      // nodes a stand-in id would sit next to the real edge as a duplicate. The
      // deterministic fallback still keeps a re-promotion from stacking copies.
      id: excerpt.excerptOfId || `excerpt-of:${excerpt.excerptId}`,
      type: 'EXCERPT_OF',
      source: excerpt.id,
      target: excerpt.conversationNodeId,
      properties: {},
    })
  }

  return {
    meta: {
      source: 'assistant',
      createdAt: new Date().toISOString(),
      caseId,
      label: 'Promoted excerpt',
    },
    nodes,
    edges,
    view: emptyViewState(),
  }
}

/**
 * Merge the excerpt into whatever viewpoint is open, creating a draft when
 * there is none. Returns where it landed so the toast can be honest about it —
 * "added to your investigation" and "started a draft" are different promises.
 */
export function landExcerptInObjectSet(
  store: StoreApi<InvestigationsState>,
  view: GraphView,
  label: string
): ExcerptLanding {
  const state = store.getState()
  const hasViewpoint = state.activeId !== null || state.transient !== null

  if (hasViewpoint) {
    // A merge that adds nothing (the excerpt is already on the canvas) still
    // counts as landed — the evidence is where the analyst expects it.
    addContributionToActive({ store, view, label })
    return 'active'
  }

  // No hard gate: the first promotion opens the draft it needs.
  openAsExploration({ store, view, label })
  return 'draft'
}
